import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoicesApi, invoiceSeriesApi } from '../services/invoices-api';
import type { CreateInvoiceData } from '../services/invoices-api';

/**
 * useInvoices — returnează facturile filtrate după params.
 *
 * ⚠️ IMPORTANT: fără filtre, returnează TOATE facturile (outgoing + incoming + toate types).
 * Pentru a nu amesteca vânzări cu achiziții, specifică întotdeauna cel puțin:
 *   - direction: 'outgoing'  → facturi emise (vânzări, pe /facturi)
 *   - direction: 'incoming'  → facturi primite (achiziții, pe /cheltuieli)
 *   - type: 'factura' | 'proforma' | 'aviz' | 'storno'
 *
 * Exemplu: useInvoices({ direction: 'outgoing', type: 'factura', page: '1' })
 */
export function useInvoices(params?: Record<string, string>, enabled = true) {
  return useQuery({
    queryKey: ['invoices', params],
    queryFn: () => invoicesApi.list(params),
    enabled,
  });
}

/**
 * useInvoicesInfinite — pentru mobile load-more (cursor pagination).
 * Acumulează rândurile peste pagini succesive. `params` NU trebuie să conțină
 * `page`/`cursor` — sunt setate intern.
 */
export function useInvoicesInfinite(params?: Record<string, string>, enabled = true) {
  const baseParams: Record<string, string> = { ...(params || {}) };
  delete baseParams.page;
  delete baseParams.cursor;

  return useInfiniteQuery({
    queryKey: ['invoices-infinite', baseParams],
    queryFn: ({ pageParam }) => {
      const queryParams: Record<string, string> = { ...baseParams };
      if (pageParam) queryParams.cursor = pageParam as string;
      else queryParams.cursor = ''; // empty cursor signals first page in cursor mode
      return invoicesApi.listCursor(queryParams);
    },
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.hasMore ? lastPage.nextCursor : undefined,
    enabled,
  });
}

export function useInvoice(id: string) {
  return useQuery({
    queryKey: ['invoices', id],
    queryFn: () => invoicesApi.get(id),
    enabled: !!id,
  });
}

export function useInvoiceStats(params?: Record<string, string>) {
  return useQuery({
    queryKey: ['invoices', 'stats', params || {}],
    queryFn: () => invoicesApi.stats(params),
  });
}

export function useCreateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateInvoiceData) => invoicesApi.create(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['efactura-deadlines'] });
    },
  });
}

export function useUpdateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateInvoiceData> & { paidAmount?: number; internalNotes?: string } }) =>
      invoicesApi.update(id, data),
    onSuccess: (updatedInvoice, vars) => {
      // Setăm cache-ul detail (`['invoices', id]`) cu valoarea returnată —
      // previne useInvoice(id) să afișeze valoarea veche o fracțiune de secundă
      // la navigare post-save (acelaşi pattern ca useUpdateCompany — commit 9f62d40d).
      qc.setQueryData(['invoices', vars.id], updatedInvoice);
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['efactura-deadlines'] });
    },
  });
}

export function useDeleteInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => invoicesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['efactura-deadlines'] });
    },
  });
}

export function useBulkDeleteInvoices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => invoicesApi.bulkDelete(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['efactura-deadlines'] });
    },
  });
}

export function useDuplicateInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => invoicesApi.duplicate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['efactura-deadlines'] });
    },
  });
}

/**
 * Seteaza/reseteaza sablon PDF persistat pentru o factura.
 * Folosit de TemplateSelectorCard din modalul /facturi cand userul apasa
 * "Salveaza" pe un sablon. templateId=null reseteaza la tenant default.
 *
 * Invalideaza detail cache si lista — dupa save modalul reciteste factura
 * cu pdfTemplateId-ul nou si iframe-ul afiseaza PDF-ul cu sablonul salvat.
 */
export function useSetInvoicePdfTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, templateId }: { id: string; templateId: string | null }) =>
      invoicesApi.setPdfTemplate(id, templateId),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['invoices', vars.id] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
    },
  });
}

export function useBulkDuplicateInvoices() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => invoicesApi.bulkDuplicate(ids),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['efactura-deadlines'] });
    },
  });
}

export function useStornoInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => invoicesApi.storno(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['efactura-deadlines'] });
    },
  });
}

export function useCancelInvoice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => invoicesApi.cancel(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['efactura-deadlines'] });
    },
  });
}

export function useBulkMarkPaid() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { ids: string[]; method?: string; date?: string; reference?: string }) =>
      invoicesApi.bulkMarkPaid(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoice-stats'] });
      qc.invalidateQueries({ queryKey: ['efactura-deadlines'] });
    },
  });
}


export function useInvoiceSeries() {
  return useQuery({
    queryKey: ['invoice-series'],
    queryFn: () => invoiceSeriesApi.list(),
  });
}
